/**
 * Plain TypeScript shapes of everything that crosses the WASM boundary (crates/gr-wasm).
 *
 * Conventions:
 * - Vectors are `{ x, y, z }` objects, never arrays.
 * - No 64-bit integers: seeds and ticks are `u32` numbers, hashes are hex strings.
 * - Ids (weapons, equipment, perks, ...) are their string slugs ("kr7", "frag", ...).
 * - Angles inside input commands are the wire values (`u16`, see `quantizeYaw`/`quantizePitch`);
 *   every angle that WASM *returns* (`PlayerSnapData`, `FxEventData`, shots) is in radians
 *   (yaw in `[0, 2π)`, positive turns left, `0` looks toward -Z; pitch positive looks up).
 */

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** Button bits of `InputCmdData.buttons` (gr_core::input::Buttons). */
export const Buttons = {
  FORWARD: 1 << 0,
  BACK: 1 << 1,
  LEFT: 1 << 2,
  RIGHT: 1 << 3,
  JUMP: 1 << 4,
  CROUCH: 1 << 5,
  WALK: 1 << 6,
  FIRE: 1 << 7,
  ADS: 1 << 8,
  RELOAD: 1 << 9,
  INTERACT: 1 << 10,
  MELEE: 1 << 11,
  LETHAL: 1 << 12,
  TACTICAL: 1 << 13,
  DROP_BOMB: 1 << 14,
} as const;

/**
 * Buttons that do nothing during the freeze time of a Defusal round (`Buttons::FREEZE_LOCKED`):
 * movement, attacks and the objective. The server clears them from every command it simulates in
 * that phase, so the predicted commands must lack them too.
 */
export const FREEZE_LOCKED =
  Buttons.FORWARD |
  Buttons.BACK |
  Buttons.LEFT |
  Buttons.RIGHT |
  Buttons.JUMP |
  Buttons.CROUCH |
  Buttons.FIRE |
  Buttons.MELEE |
  Buttons.LETHAL |
  Buttons.TACTICAL |
  Buttons.INTERACT |
  Buttons.DROP_BOMB;

/** Bits of `PlayerSnapData.flags` (gr_core::protocol::PlayerFlags). */
export const PlayerFlags = {
  ALIVE: 1 << 0,
  ON_GROUND: 1 << 1,
  CROUCHED: 1 << 2,
  SLIDING: 1 << 3,
  WALLRUN_LEFT: 1 << 4,
  WALLRUN_RIGHT: 1 << 5,
  MANTLING: 1 << 6,
  ADS: 1 << 7,
  RELOADING: 1 << 8,
  WALKING: 1 << 9,
  PLANTING: 1 << 10,
  DEFUSING: 1 << 11,
  HAS_BOMB: 1 << 12,
  SPAWN_PROTECTED: 1 << 13,
  FLASHED: 1 << 14,
} as const;

// ---- input ----

/** Click inside a tick: `frac` 0..255 position in the tick, `yaw`/`pitch` u16 view at the click instant. */
export interface FireSampleData {
  frac: number;
  yaw: number;
  pitch: number;
}

export interface InputCmdData {
  /** Server tick this input is for. */
  tick: number;
  /** `Buttons` bits. */
  buttons: number;
  /** Quantized view yaw (u16, `quantizeYaw`). */
  yaw: number;
  /** Quantized view pitch (u16, `quantizePitch`). */
  pitch: number;
  /** Requested weapon slot: 0 primary, 1 secondary, 2 melee. */
  weaponSlot: number;
  /** Present when FIRE went down during this tick. */
  fire?: FireSampleData;
  /** `pred_time - interp_render_time` in 1/256 tick units, u16. */
  viewOffset: number;
}

// ---- prediction ----

export type WeaponActionName =
  | "idle"
  | "equip"
  | "reload"
  | "reload_shell"
  | "melee"
  | "throw_lethal"
  | "throw_tactical";

/** Latest predicted player state for rendering and the HUD. */
export interface PlayerView {
  /** Tick this state is the result of. */
  tick: number;
  /** Feet position (m). */
  pos: Vec3Like;
  vel: Vec3Like;
  /** Camera height above the feet (m), already smoothed between stand/crouch/slide. */
  eyeHeight: number;
  /** Horizontal speed (m/s). */
  speed: number;
  onGround: boolean;
  crouched: boolean;
  sliding: boolean;
  /** -1 wall on the left, 1 wall on the right, 0 not wall running. */
  wallrunSide: -1 | 0 | 1;
  mantling: boolean;
  climbing: boolean;
  /** 0 standing .. 1 crouched. */
  crouchFrac: number;
  /** 0 .. 1 slide pose blend. */
  slideFrac: number;
  /** Active weapon slot: 0 primary, 1 secondary, 2 melee. */
  slot: number;
  /** Slug of the active weapon ("kr7", ..., "knife"). */
  weaponId: string;
  /** Rounds in the magazine of the active slot (0 for the knife). */
  ammo: number;
  /** Reserve rounds of the active slot. */
  reserve: number;
  /** Magazine size of the active slot. */
  magSize: number;
  action: WeaponActionName;
  reloading: boolean;
  /** Reload progress 0..1 while `reloading`, otherwise -1. */
  reloadFrac: number;
  /** Weapon equip progress 0..1 while switching, otherwise -1. */
  switchingFrac: number;
  /** 0 hip .. 1 fully aimed. */
  adsFrac: number;
  sprayIndex: number;
  bloom: number;
  /** Cone half-angle (deg) the next shot would have right now (crosshair). */
  spreadDeg: number;
  /** Field-of-view multiplier of the active weapon at full ADS. */
  adsFovMult: number;
  scopeOverlay: boolean;
  /** Active weapon has no muzzle flash (suppressor). */
  hideFlash: boolean;
  lethalLeft: number;
  tacticalLeft: number;
}

export interface MoveEventsData {
  jumped: boolean;
  /** Speed into the ground (m/s) when touching down this tick, else null. */
  landed: number | null;
  slideStarted: boolean;
  /** The slide that started this tick re-applied the boost (a fresh crouch press around touchdown). */
  slideReset: boolean;
  wallrunStarted: boolean;
  /** A wall jump or a kick off a wall. */
  wallJumped: boolean;
  /** A wall climb (or a re-arm of it) started. */
  climbStarted: boolean;
  mantled: boolean;
  /** The mantle of this tick was a ledge recovery. */
  ledgeRecovered: boolean;
  jumpPad: boolean;
  /** A hop, slide reset or wall hop this tick was perfectly timed (crosshair flash). */
  perfectHop: boolean;
}

export interface MeleeEventData {
  /** True for quick melee with a gun, false for a knife attack. */
  quick: boolean;
  subtick: number;
  damage: number;
  backstabDamage: number;
  range: number;
}

export interface ThrowEventData {
  /** Equipment slug ("frag", "flash", ...). */
  equipment: string;
  subtick: number;
}

export interface WeaponEventsData {
  melee: MeleeEventData | null;
  throws: ThrowEventData[];
  reloadStarted: boolean;
  reloadFinished: boolean;
  /** The active slot changed to this slot, else null. */
  switched: number | null;
  /** Trigger pulled on an empty magazine. */
  dryFire: boolean;
}

/** One bullet (or shotgun volley) the local player fired during a predicted tick. */
export interface PredictedShot {
  slot: number;
  weaponId: string;
  /** 0..1 position of the shot inside the tick. */
  subtick: number;
  sprayIndex: number;
  /** Per-player shot counter; with the match seed and player id it seeds the pellet spread (`shotSeed`). */
  shotCounter: number;
  /** Cone half-angle in degrees at the moment of the shot. */
  spreadDeg: number;
  /** View kick for this shot (degrees): up and to the right, `weapons::recoil_kick_deg` with the spread context at fire time. */
  kickPitchDeg: number;
  kickYawDeg: number;
  /** Eye position at the shot's sub-tick (world space). */
  origin: Vec3Like;
  /** View angles of the shot in radians (the direction the cone is centered on). */
  yaw: number;
  pitch: number;
}

export interface PredictedFrame {
  /** False: the server already confirmed this command's tick; nothing was simulated, `view` is unchanged and the events are empty. */
  simulated: boolean;
  view: PlayerView;
  moveEvents: MoveEventsData;
  shots: PredictedShot[];
  weaponEvents: WeaponEventsData;
}

/**
 * Result of `Predictor.reconcile`. When `corrected`, the predicted state was replaced by the
 * server state and the stored inputs replayed silently; `errorX/Y/Z` is `old_latest_pos -
 * new_latest_pos`, i.e. exactly the vector to add to the visual error offset so the render
 * position stays continuous (snap instead when its length exceeds 2 m).
 */
export interface ReconcileResult {
  corrected: boolean;
  /** The snapshot was ahead of everything predicted (the client had not simulated its tick yet) and became the new base. */
  adopted: boolean;
  /** Newest predicted tick before the call. */
  latestBefore: number;
  /** Inputs replayed after the server tick. */
  replayed: number;
  errorX: number;
  errorY: number;
  errorZ: number;
}

// ---- map ----

export interface RenderMeshGroup {
  material: string;
  /** xyz triples. */
  positions: Float32Array;
  /** xyz triples (flat normals). */
  normals: Float32Array;
  /** uv pairs. */
  uvs: Float32Array;
  /** Triangle list into the vertex arrays, CCW seen from outside. */
  indices: Uint32Array;
  /** Breakable groups only: `[brush, firstIndex, indexCount]` triples, one per brush. */
  brushes?: Uint32Array;
}

export interface RenderMeshData {
  /** The static geometry. */
  groups: RenderMeshGroup[];
  /** The breakable brushes (glass panes), kept out of `groups` so a broken one can be hidden. */
  breakable: RenderMeshGroup[];
}

export interface RayHitData {
  /** Distance from the origin (m). */
  dist: number;
  pos: Vec3Like;
  normal: Vec3Like;
  /** Index of the brush in the map file. */
  brush: number;
  /** `SurfaceFlags` bits. */
  surface: number;
}

export const SurfaceFlags = {
  NO_WALLRUN: 1,
  NO_CLIMB: 2,
  SLICK: 4,
  GLASS: 8,
  PLAYER_CLIP: 16,
  NO_COLLIDE: 32,
  /** A glass pane the server lets shots, melee and blasts break. */
  BREAKABLE: 64,
} as const;

// ---- unreliable channel (snapshots, pong) ----

export interface LocalPlayerData {
  /** Exact `PlayerSimState` bytes; pass to `Predictor.reconcile`. */
  sim: Uint8Array;
  health: number;
  spawnProtected: boolean;
}

export interface PlayerSnapData {
  id: number;
  /** `PlayerFlags` bits. */
  flags: number;
  /** Feet position. */
  pos: Vec3Like;
  vel: Vec3Like;
  /** Radians, `[0, 2π)`. */
  yaw: number;
  /** Radians, positive looks up. */
  pitch: number;
  /** Active weapon slug. */
  weaponId: string;
  /** 255 = hidden (enemies). */
  health: number;
}

export interface ProjectileSnapData {
  id: number;
  /** Equipment slug: "frag" | "throwing_knife" | "flash" | "smoke" | "impulse". */
  kind: string;
  owner: number;
  pos: Vec3Like;
  vel: Vec3Like;
}

export type TeamName = "none" | "a" | "b";

export type MatchPhaseName =
  | "warmup"
  | "countdown"
  | "live"
  | "round_freeze"
  | "round_live"
  | "round_end"
  | "ended";

export interface HillSnapData {
  index: number;
  owner: TeamName;
  contested: boolean;
  active: boolean;
  moveTick: number;
}

export type BombStateData =
  | { kind: "carried"; carrier: number }
  | { kind: "dropped"; pos: Vec3Like }
  | { kind: "planted"; site: number; pos: Vec3Like; explodeTick: number }
  | { kind: "defused" }
  | { kind: "exploded" };

export interface BombSnapData {
  state: BombStateData;
  /** Plant/defuse progress 0..255. */
  progress: number;
  /** Player currently planting or defusing. */
  actor: number | null;
}

export interface ModeSnapData {
  phase: MatchPhaseName;
  phaseEndTick: number;
  scores: [number, number];
  round: number;
  hill: HillSnapData | null;
  bomb: BombSnapData | null;
}

export type FxEventData =
  | {
      kind: "shot";
      shooter: number;
      /** Weapon slug. */
      weapon: string;
      origin: Vec3Like;
      /** Radians. */
      yaw: number;
      pitch: number;
      /** Cone half-angle in degrees. */
      spreadDeg: number;
      /** Pellet seed: `shotDirections(weapon, attachments, yaw, pitch, spreadDeg, seed)`. */
      seed: number;
    }
  | { kind: "melee"; attacker: number; hit: boolean }
  | { kind: "explosion"; equipment: string; pos: Vec3Like };

export interface SnapshotData {
  tick: number;
  /** Newest input tick the server has from us minus `tick`. */
  inputLead: number;
  /**
   * How early the inputs that reached the server since the previous snapshot were, in 1/960 s (`SLACK_PER_TICK` per tick): the
   * smallest of `start of the input's tick - arrival` over them; negative = an input came too late.
   * null: no input arrived since the previous snapshot.
   */
  inputSlack: number | null;
  /** null while dead or spectating. */
  local: LocalPlayerData | null;
  /** Everyone except the receiver. */
  players: PlayerSnapData[];
  projectiles: ProjectileSnapData[];
  mode: ModeSnapData;
  events: FxEventData[];
  spectating: number | null;
}

export interface PongData {
  clientTimeMs: number;
  serverTick: number;
  /** Elapsed fraction of the server's current tick in 1/256 units. */
  tickPhase: number;
}

export type DecodedUnreliable =
  | { kind: "snapshot"; snapshot: SnapshotData }
  | { kind: "pong"; pong: PongData };
